import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { CloseIcon } from '@/src/icons'

import { cn } from '@/registry/reapit/lib/utils'

/*
 * Alert / Section Message — DS alignment (Phase 3C)
 *
 * Icon size: DS icon-md = 20px (size-5), per plan/04-principal-decisions.md §Dimensions 2.
 * Colours:   resolved via --alert-* aliases that point at Phase 1 DS semantic tokens.
 *            See src/theme/globals.css §LEGACY ALERT / BADGE ALIASES.
 *
 * Grid layout:
 *   [0  / 1fr]         — no icon
 *   [size-5 / 1fr]     — with icon (has-[>svg] selector)
 */
const alertVariants = cva(
  'relative grid w-full grid-cols-[0_1fr] items-start gap-y-1 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*5)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-5 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        info: 'border-[var(--alert-info-border)] bg-[var(--alert-info-bg)] text-[var(--alert-info-foreground)] [&>svg]:text-[var(--alert-info-icon)]',
        success:
          'border-[var(--alert-success-border)] bg-[var(--alert-success-bg)] text-[var(--alert-success-foreground)] [&>svg]:text-[var(--alert-success-icon)]',
        warning:
          'border-[var(--alert-warning-border)] bg-[var(--alert-warning-bg)] text-[var(--alert-warning-foreground)] [&>svg]:text-[var(--alert-warning-icon)]',
        error:
          'border-[var(--alert-error-border)] bg-[var(--alert-error-bg)] text-[var(--alert-error-foreground)] [&>svg]:text-[var(--alert-error-icon)]',
        neutral: 'border-border bg-card text-card-foreground',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
)

type AlertProps = React.ComponentProps<'div'> &
  VariantProps<typeof alertVariants> & {
    dismissible?: boolean
    onDismiss?: () => void
  }

function Alert({ className, variant, dismissible = false, onDismiss, children, ...props }: AlertProps) {
  return (
    <div data-slot="alert" role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      {children}
      {dismissible && (
        <button
          data-slot="alert-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <CloseIcon size="sm" />
        </button>
      )}
    </div>
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  )
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn('col-start-2 grid justify-items-start gap-1 text-sm opacity-90 [&_p]:leading-relaxed', className)}
      {...props}
    />
  )
}

function AlertActions({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="alert-actions" className={cn('col-start-2 mt-2 flex gap-2', className)} {...props} />
}

export { Alert, AlertTitle, AlertDescription, AlertActions }
